import React, {useMemo} from 'react';

import {getPurposesForFolderSection} from 'archive/ArchivePurposesUtil';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {FolderSection, StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import {NavStoreRegistry} from 'constants/RegistryConstants';
import {BatchSchema, StorableWithPermissions} from 'constants/SampleConstants';
import FinderUtil from 'file-browser/FinderUtil';
import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getContainerConcentrationFilter} from 'nav/sidepanel/listing/getContainerConcentrationFilter';
import * as SearchURLs from 'nav/sidepanel/listing/SearchURLs';
import QueryingStorableCreateMenu from 'samples/sidepanel/QueryingStorableCreateMenu';
import StorableToolbar from 'samples/toolbar/StorableToolbar';
import {Schema} from 'schemas/SchemaConstants';
import {TypesFilterSchemasQuery} from 'search/filters/__generated/TypesFilterSchemasQuery';
import {FilterOption} from 'search/filters/base/FilterComponents';
import {Filter, FilterKey, ListableItem, SearchParams, SearchType, SortKey} from 'search/SearchConstants';
import * as SearchPreferences from 'search/SearchPreferences';
import withSearching, {NavigationUpdate, SearchProps} from 'search/withSearching';

import {ItemListingSchemasQuery} from './__generated/ItemListingSchemasQuery';
import ItemListing from './ItemListing';
import {ItemListingSchemasByType, SchemasFilterFunction} from './ItemListingUtil';
import StorableListingCrumbs from './StorableListingCrumbs';
import {
  DEFAULT_TYPES_FILTER,
  getListedStorableId,
  PRESERVED_FILTER_KEYS,
  resolveStorableFilter,
} from './StorablesSearchParams';

type ItemListingPlateSchema = ItemListingSchemasQuery['plateSchemas'][number];
type ItemListingLocationSchema = ItemListingSchemasQuery['locationSchemas'][number];
type ItemListingContainerSchema = ItemListingSchemasQuery['containerSchemas'][number];
type ItemListingBoxSchema = ItemListingSchemasQuery['boxSchemas'][number];
type TypesFilterPlateSchema = TypesFilterSchemasQuery['plateSchemas'][number];
type TypesFilterLocationSchema = TypesFilterSchemasQuery['locationSchemas'][number];
type TypesFilterContainerSchema = TypesFilterSchemasQuery['containerSchemas'][number];
type TypesFilterBoxSchema = TypesFilterSchemasQuery['boxSchemas'][number];

interface StorablesListingProps {
  activeItemId: string | null;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  isExpanded: boolean;
  onNavigateToRoot: () => void;
  onListRegistryFolder: (folder: StringIDFolder) => void;
  registry: NavStoreRegistry;
}

function getStorablesURL(
  prevProps: StorablesListingProps & SearchProps,
  props: StorablesListingProps & SearchProps
): NavigationUpdate | null {
  if (!props.isExpanded) {
    return null;
  }
  return {url: SearchURLs.getNavigationURL('/storage', props.searchParams, props.filterErrors)};
}

function renderStorableToolbar(items: Array<ListableItem> | null, selectedIds: Array<string>): JSX.Element {
  return (
    <StorableToolbar
      isLoading={items == null}
      storables={items as Array<StorableWithPermissions>}
      selectedIds={selectedIds}
    />
  );
}

function getSchemasRegistryFilter(registryId: string): SchemasFilterFunction {
  return (schemasByAvailableType: ItemListingSchemasByType): ItemListingSchemasByType => {
    const filteredSchemas: ItemListingSchemasByType = {};
    for (const type of Object.keys(schemasByAvailableType)) {
      const storableSchemas = schemasByAvailableType[type] as Array<
        | ItemListingBoxSchema
        | ItemListingContainerSchema
        | ItemListingLocationSchema
        | ItemListingPlateSchema
        | TypesFilterBoxSchema
        | TypesFilterContainerSchema
        | TypesFilterLocationSchema
        | TypesFilterPlateSchema
      >;
      filteredSchemas[type] = storableSchemas.filter((schema) => schema.registry?.id === registryId);
    }

    return filteredSchemas;
  };
}

export function StorablesListing(props: StorablesListingProps & SearchProps): JSX.Element {
  const _handleSearch = (searchParams: SearchParams): void => {
    const newSearchParams = resolveStorableFilter(searchParams);
    props.onSearch(newSearchParams);
    SearchPreferences.rememberAppliedSortsToUserPreference(
      NavConstants.Section.STORAGE,
      newSearchParams.sorts
    );
  };

  // TODO(Search): resolveStorableFilter is filter logic that depends on multiple search parameters and is
  // specific to this listing. This is an exception to the normal listing behavior that is able to be
  // moved to the reducers. We're hacking around by merging the new filter in based on the full props,
  // and possibly performing multple dispatches, but this is not a great long term solution. Revisit this
  // once we are fully migrated to the new granular actions and have time to consider a better design.
  const _handleChangeQuery = (query: string): void => {
    const searchParams = {...props.searchParams, query};
    const newSearchParams = resolveStorableFilter(searchParams);
    props.onUpdateQueryString(newSearchParams.query);
    props.onUpdateFilters(newSearchParams.filters);
  };

  const _handleChangeFilters = (filters: Array<Filter>): void => {
    const searchParams = {...props.searchParams, filters};
    props.onUpdateFilters(resolveStorableFilter(searchParams).filters);
  };

  const schemasRegistryFilter: SchemasFilterFunction = useMemo(
    () => getSchemasRegistryFilter(props.registry.id),
    [props.registry.id]
  );

  const listedStorableId = getListedStorableId(props.searchParams.filters);

  return (
    <div className="sidepanel">
      <ItemListing
        {...props}
        onSearch={_handleSearch}
        onUpdateQueryString={_handleChangeQuery}
        onUpdateFilters={_handleChangeFilters}
        actionSource={ActionOrigin.INVENTORY_EXPANDED_SEARCH_TABLE}
        savedSearchSection={SearchSection.STORAGE}
        availableTypes={[
          ModelTypeConstants.CONTAINER,
          ModelTypeConstants.BOX,
          ModelTypeConstants.PLATE,
          ModelTypeConstants.LOCATION,
        ]}
        defaultTypes={DEFAULT_TYPES_FILTER.value}
        // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
        makeCrumbs={(settingsComponents?: JSX.Element) => (
          <StorableListingCrumbs
            registry={props.registry}
            onNavigateToRegistry={() => {
              props.onListRegistryFolder(FinderUtil.sourceToRootFolder(props.registry));
            }}
            onNavigateToRoot={props.onNavigateToRoot}
            onNavigateToStorable={_handleChangeFilters}
            searchParams={props.searchParams}
            storableId={listedStorableId}
            activeSavedSearch={props.activeSavedSearch}
            settingsComponents={settingsComponents}
          />
        )}
        additionalHeaderButtons={
          <QueryingStorableCreateMenu
            registry={props.registry}
            isExpanded={props.isExpanded}
            listedLocationId={listedStorableId}
          />
        }
        extraPrimaryFilterOptions={[{filterKey: FilterKey.BARCODES}]}
        getSecondaryFilterGroups={(selectedSchema: Schema | BatchSchema | null) => [
          {
            name: 'Container',
            filterOptions: [
              {filterKey: FilterKey.NAMES},
              {filterKey: FilterKey.CREATED_AT},
              {filterKey: FilterKey.MODIFIED_AT},
              {filterKey: FilterKey.API_IDS},
              {filterKey: FilterKey.CREATOR},
              {
                filterKey: FilterKey.SCHEMA_FIELD,
                extraProps: {schemaFields: selectedSchema ? selectedSchema.fields : []},
              },
              {filterKey: FilterKey.CONTAINER_QUANTITY},
              ...getContainerConcentrationFilter(),
              {filterKey: FilterKey.STARRED},
              {
                filterKey: FilterKey.ARCHIVE_PURPOSES,
                extraProps: {archivePurposes: getPurposesForFolderSection(FolderSection.INVENTORY)},
              },
              {filterKey: FilterKey.SAMPLE_RESTRICTION_ACCESS},
              {filterKey: FilterKey.SAMPLE_RESTRICTION_STATUS},
              {filterKey: FilterKey.SAMPLE_AUTHORIZED_USERS},
              {filterKey: FilterKey.SAMPLE_OWNERS},
              ...(BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE
                ? [
                    {filterKey: FilterKey.EXPIRATION_DATE} satisfies FilterOption,
                    {filterKey: FilterKey.IS_EXPIRED} satisfies FilterOption,
                  ]
                : []),
              ...(BenchlingConfig.INVENTORY_ENABLE_FREEZE_THAW
                ? [{filterKey: FilterKey.FREEZE_THAW_COUNT} satisfies FilterOption]
                : []),
            ],
          },
          {
            name: 'Container contents',
            filterOptions: [
              {filterKey: FilterKey.CONTAINER_CONTENTS},
              {filterKey: FilterKey.CONTAINER_CONTENT_SCHEMA},
            ],
          },
          {
            name: 'Box/Plate',
            filterOptions: [
              {filterKey: FilterKey.GRID_EMPTY_CONTAINERS},
              {filterKey: FilterKey.GRID_EMPTY_POSITIONS},
            ],
          },
          {
            name: 'Check-out',
            filterOptions: [
              {filterKey: FilterKey.CONTAINER_CHECK_OUT_STATUS},
              {filterKey: FilterKey.CONTAINER_CHECK_OUT_ASSIGNEE},
            ],
          },
          {
            name: 'Location validation',
            filterOptions: [
              {filterKey: FilterKey.INVENTORY_VALIDATION_STATUS},
              {filterKey: FilterKey.INVENTORY_VALIDATION_ERROR_LABEL},
            ],
          },
        ]}
        availableSortKeys={[
          SortKey.SORT_BARCODE_KEY,
          SortKey.SORT_NAME_KEY,
          SortKey.SORT_MODIFIED_AT_KEY,
          SortKey.SORT_CREATED_AT_KEY,
        ]}
        preservedFilterKeys={PRESERVED_FILTER_KEYS}
        section={NavConstants.Section.STORAGE}
        dataTestAttrs={{component: 'StorablesListing', element: 'root'}}
        renderToolbar={renderStorableToolbar}
        filterSchemas={schemasRegistryFilter}
      />
    </div>
  );
}

export default withSearching(StorablesListing, SearchType.STORABLE, NavConstants.Section.STORAGE, {
  getNavigationUpdate: getStorablesURL,
});
